import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Pencil,
  Search,
  ListFilter,
  UserPlus,
  Mail,
  Download,
  Upload,
  Lock,
  LockOpen,
  Trash2,
  UserRoundPlus,
  GraduationCap,
  UserIcon,
  Users,
  TextAlignJustify,
  ListEnd,
  SquarePen,
} from "lucide-react";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import TabsCst from "@components/TabsCst";
import InputCst from "@components/InputCst/InputCst";
import SortMenuCst from "@components/SortMenuCst";
import FiltersAccordionCst from "@components/FiltersAccordionCst";
import BulkActionBarCst from "@components/BulkActionBarCst";
import { BulkAction } from "@components/BulkActionBarCst/types";
import PaginatorCst from "@components/PaginatorCst";
import UploadModalCst from "@components/UploadModalCst";
import AsyncProcessBanner from "@components/AsyncProcessBanner";
import {
  openCreatePicker,
  useCanCreate,
} from "@components/CreateOptionPickerCst/host";
import {
  usePaginatedList,
  PaginatedListFilters,
} from "@hooks/usePaginatedList";
import { useAsyncProcess } from "@hooks/useAsyncProcess";
import { useNavbarInitiative } from "@hooks/useNavbarInitiative";
import { ServicesURL, downloadFile } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { PagesID } from "@utils/utilsConfigurations";
import { AsyncProcessStatusEnum } from "src/interfaces/AsyncProcess";
import { buildUGListUrl, UGTabKey } from "./types";
import { useUsersGroupsPageState } from "./hooks/useUsersGroupsPageState";
import { useUsersGroupsActions } from "./hooks/useUsersGroupsActions";
import UserItem from "./components/UserItem";
import GroupItem from "./components/GroupItem";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";

const PAGE_SIZE = 20;

const UsersGroupsV2: React.FC = () => {
  const { t } = useTranslation("users-groups-v2");
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: corporateId = "" } = useParams();
  const { selectedInitiative } = useNavbarInitiative();
  const canCreate = useCanCreate("users-groups");

  const { activeTab, search, fieldFilters, sort, page, setActiveTab, update } =
    useUsersGroupsPageState();

  const isUsersTab = activeTab === "users";

  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(
    new Set(),
  );
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const hasActiveFilters = useMemo(
    () =>
      Object.values(fieldFilters || {}).some((val) => {
        if (val === undefined || val === null || val === "") return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
      }),
    [fieldFilters],
  );

  const [uploadOpen, setUploadOpen] = useState(false);

  const initiativeFilter =
    isUsersTab && selectedInitiative && selectedInitiative !== "all"
      ? selectedInitiative
      : undefined;

  const listFilters: PaginatedListFilters = useMemo(
    () => ({
      page,
      pageSize,
      search: search || undefined,
      fieldFilters: initiativeFilter
        ? { ...fieldFilters, initiative_id: initiativeFilter }
        : fieldFilters,
      sort,
    }),
    [page, pageSize, search, fieldFilters, sort, initiativeFilter],
  );

  const prevInitiativeRef = useRef(selectedInitiative);
  useEffect(() => {
    if (prevInitiativeRef.current !== selectedInitiative) {
      prevInitiativeRef.current = selectedInitiative;
      if (isUsersTab && page !== 1) update({ page: 1 });
    }
  }, [selectedInitiative, isUsersTab, page, update]);

  const listUrl = useMemo(
    () => buildUGListUrl(activeTab, corporateId),
    [activeTab, corporateId],
  );

  const { items, totalItems, totalPages, isLoading, mutate } = usePaginatedList(
    listUrl,
    listFilters,
  );

  const groupKind = activeTab === "static-groups" ? "STATIC" : "DYNAMIC";

  const {
    userActions,
    getGroupActions,
    openEnrollUser,
    openEnrollGroup,
    openWelcomeMail,
    openBlockUser,
    openBulkStatus,
    openDeleteUser,
    openDeleteGroup,
    openAddUsersToGroup,
    modals,
  } = useUsersGroupsActions({
    corporateId,
    onRefresh: () => mutate && mutate(),
    onClearSelection: () => setSelectedIds(new Set()),
  });

  const groupActions = getGroupActions(groupKind);

  const {
    currentProcess,
    isUploadInProgress,
    progressPercentage,
    startAsyncUpload,
    downloadDiscardsCsv,
    dismissProcess,
  } = useAsyncProcess({
    processType: "USER_UPLOAD",
    corporateId,
    onCompleted: () => mutate && mutate(),
  });

  const getItemId = (item: any): string | number =>
    isUsersTab ? item.id_user : item.id;

  const handleTabChange = (next: string) => {
    setActiveTab(next as UGTabKey);
    setSelectedIds(new Set());
  };

  // TODO: selezione multipla fuori wave 1, per ripristinarla riattivare la
  // logica commentata qui sotto e il checkbox master nella BulkActionBarCst.
  const handleSelectToggle = (itemId: string | number, next: boolean) => {
    setSelectedIds(next ? new Set([itemId]) : new Set());
    // setSelectedIds((prev) => {
    //   const copy = new Set(prev);
    //   if (next) copy.add(itemId);
    //   else copy.delete(itemId);
    //   return copy;
    // });
  };

  // const handleToggleAll = (next: boolean) => {
  //   setSelectedIds(next ? new Set(items.map(getItemId)) : new Set());
  // };

  // const allChecked = items.length > 0 && selectedIds.size === items.length;
  // const someChecked = selectedIds.size > 0 && !allChecked;

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, search, fieldFilters, sort, selectedInitiative]);

  const tabs = [
    { key: "users", label: t("tabs.users"), icon: <UserIcon size={16} /> },
    {
      key: "static-groups",
      label: t("tabs.staticGroups"),
      icon: <TextAlignJustify size={16} />,
    },
    {
      key: "dynamic-groups",
      label: t("tabs.dynamicGroups"),
      icon: <ListEnd size={16} />,
    },
  ];

  const sortOptions = [
    { label: t("sort.mostRecent"), value: "most-recent" },
    { label: t("sort.leastRecent"), value: "least-recent" },
    { label: t("sort.alphabetical"), value: "alphabetical" },
  ];

  const handleBulkViewLO = () => {
    const selectedItem = items.find((it: any) =>
      selectedIds.has(getItemId(it)),
    );
    if (!selectedItem) return;
    if (isUsersTab) userActions.onViewLO(selectedItem);
    else groupActions.onViewLO(selectedItem);
  };

  const handleBulkAddUsers = () => {
    const selectedGroup = items.find((it: any) =>
      selectedIds.has(getItemId(it)),
    );
    if (selectedGroup) openAddUsersToGroup(selectedGroup);
  };

  const handleBulkEnroll = () => {
    const selectedItem = items.find((it: any) =>
      selectedIds.has(getItemId(it)),
    );
    if (!selectedItem) return;
    if (isUsersTab) openEnrollUser(selectedItem);
    else openEnrollGroup(selectedItem, groupKind);
  };

  const handleDownloadCsv = async () => {
    try {
      const response = await fetcher<string>(
        `${ServicesURL.getCustomersDownloadV3}?corporate_id=${corporateId}&type=LEARNER`,
        MethodType.GET,
      );
      if (response) downloadFile(response);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownloadTemplate = () =>
    downloadFile(
      `${ServicesURL.getCustomersDownloadTemplate}?corporate_id=${corporateId}&type=LEARNER`,
    );

  const handleUploadUsersCsv = (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return startAsyncUpload(formData);
  };

  const bulkActions: BulkAction[] = useMemo(() => {
    if (activeTab === "users") {
      return [
        {
          key: "enroll",
          label: t("bulk.enroll"),
          icon: <UserPlus size={14} />,
          onClick: handleBulkEnroll,
          disabled: selectedIds.size !== 1,
        },
        {
          key: "welcome-mail",
          label: t("bulk.welcomeMail"),
          icon: <Mail size={14} />,
          onClick: () => openWelcomeMail(),
          alwaysEnabled: true,
        },
        {
          key: "download-csv",
          label: t("bulk.downloadCsv"),
          icon: <Download size={14} />,
          onClick: () => {
            const selectedUser = items.find((it: any) =>
              selectedIds.has(getItemId(it)),
            );
            if (selectedUser) userActions.onDownloadCsv(selectedUser);
          },
          disabled: selectedIds.size !== 1,
        },
        {
          key: "upload-users",
          label: t("bulk.uploadUsers"),
          icon: <Upload size={14} />,
          onClick: () => setUploadOpen(true),
          alwaysEnabled: true,
          disabled: isUploadInProgress,
        },
      ];
    }
    if (activeTab === "static-groups") {
      return [
        {
          key: "enroll",
          label: t("bulk.enroll"),
          icon: <UserPlus size={14} />,
          onClick: handleBulkEnroll,
          disabled: selectedIds.size !== 1,
        },
        {
          key: "add-users",
          label: t("bulk.addUsers"),
          icon: <UserRoundPlus size={14} />,
          onClick: handleBulkAddUsers,
          disabled: selectedIds.size !== 1,
        },
        {
          key: "show-lo",
          label: t("bulk.showLearningObjects"),
          icon: <GraduationCap size={14} />,
          onClick: handleBulkViewLO,
          disabled: selectedIds.size !== 1,
        },
      ];
    }
    return [
      {
        key: "enroll",
        label: t("bulk.enroll"),
        icon: <UserPlus size={14} />,
        onClick: handleBulkEnroll,
        disabled: selectedIds.size !== 1,
      },
      {
        key: "show-lo",
        label: t("bulk.showLearningObjects"),
        icon: <GraduationCap size={14} />,
        onClick: handleBulkViewLO,
        disabled: selectedIds.size !== 1,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    t,
    corporateId,
    startAsyncUpload,
    isUploadInProgress,
    selectedIds,
    items,
  ]);

  const singleSelectedId =
    selectedIds.size === 1 ? Array.from(selectedIds)[0] : null;

  const singleSelectedItem =
    singleSelectedId != null
      ? items.find((it: any) => getItemId(it) === singleSelectedId)
      : undefined;

  const selectedUsers = isUsersTab
    ? items.filter((it: any) => selectedIds.has(getItemId(it)))
    : [];
  const blockedSelectedCount = selectedUsers.filter(
    (u: any) => String(u.status).toLowerCase() === "suspended",
  ).length;
  const prevailingBlocked =
    selectedUsers.length > 0 && blockedSelectedCount * 2 > selectedUsers.length;

  const onBulkLock = () => {
    if (selectedIds.size === 0) return;
    if (selectedIds.size === 1 && singleSelectedItem) {
      openBlockUser(singleSelectedItem);
      return;
    }
    openBulkStatus(
      prevailingBlocked ? "ACTIVE" : "SUSPENDED",
      Array.from(selectedIds),
    );
  };

  const handleBulkEdit = () => {
    if (singleSelectedId == null) return;
    if (activeTab === "users") {
      navigate(
        `/v2/corporate/${corporateId}/detail/users-groups/${singleSelectedId}/edit`,
      );
    } else if (activeTab === "static-groups") {
      navigate(
        `/v2/corporate/${corporateId}/detail/users-groups/groups/${singleSelectedId}/edit`,
      );
    } else {
      navigate(
        `/v2/corporate/${corporateId}/detail/users-groups/dynamic-groups/${singleSelectedId}/edit`,
      );
    }
  };

  const bulkIconActions = [
    ...(singleSelectedId != null
      ? [
          {
            key: "edit",
            icon: <Pencil size={18} />,
            onClick: handleBulkEdit,
            tooltip: t("bulk.edit") || "",
          },
        ]
      : []),
    ...(isUsersTab
      ? [
          {
            key: "lock",
            icon: prevailingBlocked ? (
              <LockOpen size={18} />
            ) : (
              <Lock size={18} />
            ),
            onClick: onBulkLock,
            disabled: selectedIds.size === 0,
            tooltip:
              (prevailingBlocked ? t("bulk.unblock") : t("bulk.block")) || "",
          },
        ]
      : []),
    {
      key: "delete",
      icon: <Trash2 size={18} />,
      onClick: () => {
        if (!singleSelectedItem) return;
        if (isUsersTab) openDeleteUser(singleSelectedItem);
        else openDeleteGroup(singleSelectedItem, groupKind);
      },
      disabled: !singleSelectedItem,
      tooltip: t("bulk.delete") || "",
    },
  ];

  return (
    <AnimationFadeIn>
      <>
        <Box sx={{ pr: 3 }}>
          <Breadcrumb />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "1.5rem",
                  lineHeight: "1.875rem",
                  fontWeight: 700,
                  color: theme.palette.exploreCardText,
                }}
              >
                {t("title")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "1rem",
                  lineHeight: "1.125rem",
                  color: theme.palette.exploreCardText,
                  mt: "0.5rem",
                }}
              >
                {t("description")}
              </Typography>
            </Box>
            <ButtonPrimary
              size="md"
              startIcon={<SquarePen size={"1rem"} />}
              onClick={() => openCreatePicker("users-groups")}
              disabled={!canCreate}
            >
              {t("createButton")}
            </ButtonPrimary>
          </Box>

          <Box sx={{ mb: 3 }}>
            <TabsCst
              tabs={tabs}
              activeKey={activeTab}
              onChange={handleTabChange}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 240,
                "& .MuiFormControl-root": {
                  minWidth: "0 !important",
                  width: "100% !important",
                },
                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                "& .MuiFormHelperText-root": { display: "none" },
                "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
              }}
            >
              <InputCst
                id="users-groups-search"
                placeholder={t("searchPlaceholder") || ""}
                value={search ?? ""}
                onChange={(e: any) =>
                  update({ search: e.target.value, page: 1 })
                }
                inputSearch
                startAdornment={
                  <Search
                    size={16}
                    color={theme.palette.systemSecondary01}
                    style={{ marginRight: 8 }}
                  />
                }
              />
            </Box>

            {isUsersTab ? (
              <ButtonSecondary
                size="md"
                endIcon={<ListFilter size={16} />}
                onClick={() => setFiltersPanelOpen((prev) => !prev)}
                sxButton={
                  hasActiveFilters || filtersPanelOpen
                    ? {
                        border: `2px solid ${theme.palette.systemSecondary01} !important`,
                      }
                    : undefined
                }
              >
                {t("filtersButton")}
              </ButtonSecondary>
            ) : (
              <>
                <Box
                  sx={{
                    width: 200,
                    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    "& .MuiFormHelperText-root": { display: "none" },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "8px",
                    },
                  }}
                >
                  <InputCst
                    id="users-groups-id-group"
                    label={t("idGroup") || ""}
                    value={(fieldFilters.id as string) ?? ""}
                    onChange={(e: any) =>
                      update({
                        fieldFilters: { ...fieldFilters, id: e.target.value },
                        page: 1,
                      })
                    }
                  />
                </Box>
                <Box
                  sx={{
                    width: 200,
                    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    "& .MuiFormHelperText-root": { display: "none" },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "8px",
                    },
                  }}
                >
                  <InputCst
                    id="users-groups-name-group"
                    label={t("nameGroup") || ""}
                    value={(fieldFilters.name as string) ?? ""}
                    onChange={(e: any) =>
                      update({
                        fieldFilters: { ...fieldFilters, name: e.target.value },
                        page: 1,
                      })
                    }
                  />
                </Box>
              </>
            )}

            {/* <SortMenuCst
              id="users-groups-sort"
              value={sort ?? "most-recent"}
              options={sortOptions}
              onChange={(next) => update({ sort: next || null })}
            /> */}
          </Box>

          {isUsersTab && (
            <FiltersAccordionCst
              open={filtersPanelOpen}
              onClose={() => setFiltersPanelOpen(false)}
              pageId={PagesID["corporate.customers"]}
              value={fieldFilters}
              onApply={(next) => update({ fieldFilters: next, page: 1 })}
            />
          )}

          <Box sx={{ mb: 2 }}>
            <BulkActionBarCst
              selectedCount={selectedIds.size}
              // checked={allChecked}
              // indeterminate={someChecked}
              // onToggleAll={handleToggleAll}
              selectedLabel={(count) => t("bulk.selected", { count })}
              actions={bulkActions}
              iconActions={bulkIconActions}
            />
          </Box>

          {isUsersTab && currentProcess && (
            <AsyncProcessBanner
              process={currentProcess}
              progressPercentage={progressPercentage}
              onDownloadDiscards={
                currentProcess.status ===
                AsyncProcessStatusEnum.COMPLETED_WITH_ERRORS
                  ? () => downloadDiscardsCsv(currentProcess.processUuid)
                  : undefined
              }
              onDismiss={dismissProcess}
            />
          )}

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 6,
                color: theme.palette.text.primary,
              }}
            >
              {t("empty")}
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {items.map((item: any) => {
                const itemId = getItemId(item);
                const selected = selectedIds.has(itemId);
                if (isUsersTab) {
                  return (
                    <UserItem
                      key={`user-${itemId}`}
                      user={item}
                      corporateId={corporateId}
                      selected={selected}
                      onSelect={(next) => handleSelectToggle(itemId, next)}
                      {...userActions}
                    />
                  );
                }
                return (
                  <GroupItem
                    key={`group-${itemId}`}
                    group={item}
                    entityType={
                      activeTab === "static-groups"
                        ? "group-static"
                        : "group-dynamic"
                    }
                    corporateId={corporateId}
                    selected={selected}
                    showCreationDate
                    onSelect={(next) => handleSelectToggle(itemId, next)}
                    {...groupActions}
                  />
                );
              })}
            </Box>
          )}

          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <PaginatorCst
                page={page}
                pageSize={pageSize}
                totalItems={totalItems}
                onPageChange={(next) => update({ page: next })}
                onPageSizeChange={(next) => {
                  setPageSize(next);
                  update({ page: 1 });
                }}
              />
            </Box>
          )}
        </Box>

        <UploadModalCst
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          title={String(t("upload-users-modal.title"))}
          accept=".csv,text/csv"
          banner={{
            text: String(t("upload-users-modal.banner-text")),
            downloadLabel: String(t("upload-users-modal.banner-download-full")),
            onDownload: handleDownloadCsv,
            downloadVariant: "contained",
            downloadIcon: <Users size={16} />,
            secondaryDownloadLabel: String(
              t("upload-users-modal.banner-download"),
            ),
            onSecondaryDownload: handleDownloadTemplate,
          }}
          onUpload={handleUploadUsersCsv}
          successMessage={String(
            t("async-upload-started", { ns: "async-process" }),
          )}
        />

        {modals}
      </>
    </AnimationFadeIn>
  );
};

export default UsersGroupsV2;
